import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { LUXURY_BRANDS } from '../lib/brands';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, LogOut, X, Loader2, Upload } from 'lucide-react';

const MAX_FILES = 10;
const MAX_SIZE = 8 * 1024 * 1024; // 8 MB
const ACCEPT_IMG = ['image/jpeg', 'image/png', 'image/webp'];

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const EMPTY = {
  brand: '', brand_custom: '', model: '', reference: '', year: '', condition: 'unworn', availability: 'in_stock',
  price: '', currency: 'EUR',
  description: '', images_list: [], box: true, papers: true, movement: '', case_material: '',
  case_size: '', dial_color: '', featured: false,
};

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState('watches');
  const [watches, setWatches] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | watch obj
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const loadAll = async () => {
    try {
      const [w, s, c] = await Promise.all([
        api.get('/watches'),
        api.get('/submissions').catch(() => ({ data: [] })),
        api.get('/contact').catch(() => ({ data: [] })),
      ]);
      setWatches(w.data); setSubmissions(s.data); setContacts(c.data);
    } catch { /* noop */ }
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  if (loading) return <div className="text-center py-32 text-[#c9a961] font-serif text-2xl">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const openNew = () => { setEditing('new'); setForm(EMPTY); };
  const openEdit = (w) => {
    const isCustomBrand = w.brand && !LUXURY_BRANDS.includes(w.brand);
    setEditing(w);
    setForm({
      ...EMPTY, ...w,
      brand: isCustomBrand ? '__custom__' : (w.brand || ''),
      brand_custom: isCustomBrand ? w.brand : '',
      year: w.year || '',
      images_list: Array.isArray(w.images) ? w.images : [],
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const slots = MAX_FILES - form.images_list.length;
    if (slots <= 0) { toast.error(`Max ${MAX_FILES} immagini`); return; }
    const taken = files.slice(0, slots);
    const accepted = taken.filter((f) => ACCEPT_IMG.includes(f.type) && f.size <= MAX_SIZE);
    if (accepted.length < taken.length) toast.message('Alcuni file scartati (formato o > 8 MB)');
    const dataUrls = await Promise.all(accepted.map(readAsDataURL));
    setForm((f) => ({ ...f, images_list: [...f.images_list, ...dataUrls] }));
  };
  const removeImage = (idx) => setForm((f) => ({ ...f, images_list: f.images_list.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const finalBrand = form.brand === '__custom__' ? form.brand_custom.trim() : form.brand;
      if (!finalBrand) { toast.error('Seleziona un brand'); setSaving(false); return; }
      const payload = {
        brand: finalBrand,
        model: form.model,
        reference: form.reference,
        year: form.year ? parseInt(form.year, 10) : null,
        condition: form.condition,
        availability: form.availability,
        price: form.availability === 'on_request' ? (parseFloat(form.price) || 0) : parseFloat(form.price),
        currency: form.currency,
        description: form.description,
        images: form.images_list,
        box: form.box, papers: form.papers,
        movement: form.movement, case_material: form.case_material,
        case_size: form.case_size, dial_color: form.dial_color,
        featured: form.featured,
      };
      if (editing === 'new') {
        await api.post('/watches', payload);
        toast.success('Watch added');
      } else {
        await api.put(`/watches/${editing.id}`, payload);
        toast.success('Watch updated');
      }
      close(); await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`${t('admin.confirm_delete')} ${w.brand} ${w.model}?`)) return;
    try { await api.delete(`/watches/${w.id}`); toast.success('Deleted'); await loadAll(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
        <div>
          <p className="gn-overline mb-2">{t('admin.dashboard')}</p>
          <h1 className="font-serif text-4xl text-[#f5f5f0]">{user.name || user.email}</h1>
        </div>
        <button onClick={logout} className="gn-btn-ghost" data-testid="admin-logout"><LogOut size={14} /> {t('admin.logout')}</button>
      </div>

      <div className="flex gap-1 border-b border-[#c9a961]/20 mb-10">
        {[
          ['watches', `${t('admin.watches')} (${watches.length})`],
          ['submissions', `${t('admin.submissions')} (${submissions.length})`],
          ['contacts', `${t('admin.contacts')} (${contacts.length})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-6 py-3 text-xs tracking-[0.2em] uppercase border-b-2 -mb-px ${tab === k ? 'border-[#c9a961] text-[#c9a961]' : 'border-transparent text-[#a3b5ad] hover:text-[#f5f5f0]'}`} data-testid={`admin-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'watches' && (
        <div>
          <button onClick={openNew} className="gn-btn-primary mb-8" data-testid="admin-add-watch"><Plus size={14} /> {t('admin.add_watch')}</button>
          <div className="space-y-2">
            {watches.map((w) => (
              <div key={w.id} className="grid grid-cols-12 gap-4 items-center border border-[#c9a961]/15 p-4 hover:bg-[#0d2a1f]/40" data-testid={`admin-watch-row-${w.id}`}>
                <div className="col-span-1 aspect-square overflow-hidden bg-[#05150f]">
                  {w.images?.[0] && <img src={w.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="col-span-4">
                  <p className="font-serif text-lg text-[#f5f5f0]">{w.brand} {w.model}</p>
                  <p className="text-xs text-[#a3b5ad]">{w.reference} · {w.year}</p>
                </div>
                <div className="col-span-2 text-sm text-[#a3b5ad]">{w.condition}</div>
                <div className="col-span-2 font-serif text-[#c9a961]">{new Intl.NumberFormat('it-CH', { style: 'currency', currency: w.currency, maximumFractionDigits: 0 }).format(w.price)}</div>
                <div className="col-span-1 text-xs">{w.featured && <span className="text-[#c9a961]">★</span>} {w.sold && <span className="text-[#9c4b4b]">●</span>}</div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button onClick={() => openEdit(w)} className="p-2 text-[#c9a961] hover:bg-[#0d2a1f]" data-testid={`admin-edit-${w.id}`}><Edit3 size={16} /></button>
                  <button onClick={() => remove(w)} className="p-2 text-[#9c4b4b] hover:bg-[#0d2a1f]" data-testid={`admin-delete-${w.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'submissions' && (
        <div className="space-y-3" data-testid="admin-submissions">
          {submissions.length === 0 && <p className="text-[#a3b5ad]">—</p>}
          {submissions.map((s) => (
            <div key={s.id} className="border border-[#c9a961]/15 p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-serif text-lg text-[#f5f5f0]">{s.brand} {s.model}</p>
                  <p className="text-xs text-[#a3b5ad]">{new Date(s.created_at).toLocaleString()}</p>
                </div>
                {s.price_expectation && <p className="font-serif text-xl text-[#c9a961]">€ {s.price_expectation.toLocaleString()}</p>}
              </div>
              <p className="text-sm text-[#a3b5ad] mb-2"><strong className="text-[#f5f5f0]">{s.name}</strong> · {s.email} · {s.phone}</p>
              {s.description && <p className="text-sm text-[#a3b5ad] mt-3 italic">"{s.description}"</p>}
              {s.images && s.images.length > 0 && (
                <div className="mt-4 grid grid-cols-4 md:grid-cols-8 gap-2" data-testid={`admin-submission-photos-${s.id}`}>
                  {s.images.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden border border-[#c9a961]/20 hover:border-[#c9a961]">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'contacts' && (
        <div className="space-y-3" data-testid="admin-contacts">
          {contacts.length === 0 && <p className="text-[#a3b5ad]">—</p>}
          {contacts.map((c) => (
            <div key={c.id} className="border border-[#c9a961]/15 p-5">
              <div className="flex justify-between items-start mb-3">
                <p className="font-serif text-lg text-[#f5f5f0]">{c.subject || '—'}</p>
                <p className="text-xs text-[#a3b5ad]">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <p className="text-sm text-[#a3b5ad] mb-2"><strong className="text-[#f5f5f0]">{c.name}</strong> · {c.email} · {c.phone}</p>
              <p className="text-sm text-[#a3b5ad] mt-3">{c.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {editing && (
        <div className="fixed inset-0 bg-[#05150f]/90 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-12 px-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#08231a] border border-[#c9a961]/30 max-w-2xl w-full p-8 my-12" data-testid="admin-watch-form">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl text-[#f5f5f0]">{editing === 'new' ? t('admin.add_watch') : t('admin.edit')}</h2>
              <button type="button" onClick={close} className="text-[#a3b5ad] hover:text-[#c9a961]"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <label className="gn-overline mb-2 block">Brand *</label>
                <select required className="gn-input bg-[#05150f]" value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} data-testid="admin-brand-select">
                  <option value="">— Seleziona —</option>
                  {LUXURY_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  <option value="__custom__">+ Altro brand…</option>
                </select>
                {form.brand === '__custom__' && (
                  <input
                    className="gn-input mt-2"
                    placeholder="Nome del brand"
                    value={form.brand_custom}
                    onChange={(e) => setForm({...form, brand_custom: e.target.value})}
                    data-testid="admin-brand-custom"
                  />
                )}
              </div>
              <div><label className="gn-overline mb-2 block">Model *</label><input required className="gn-input" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Reference</label><input className="gn-input" value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Year</label><input type="number" className="gn-input" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Condition</label>
                <select className="gn-input bg-[#05150f]" value={form.condition} onChange={(e) => setForm({...form, condition: e.target.value})}>
                  <option value="new">new</option><option value="unworn">unworn</option>
                  <option value="excellent">excellent</option><option value="good">good</option>
                </select>
              </div>
              <div><label className="gn-overline mb-2 block">Availability</label>
                <select className="gn-input bg-[#05150f]" value={form.availability} onChange={(e) => setForm({...form, availability: e.target.value})} data-testid="admin-availability">
                  <option value="in_stock">In Stock</option>
                  <option value="on_request">On Request</option>
                </select>
              </div>
              <div><label className="gn-overline mb-2 block">Price *</label><input required type="number" step="0.01" className="gn-input" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Currency</label>
                <select className="gn-input bg-[#05150f]" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})}>
                  <option>EUR</option><option>CHF</option><option>USD</option><option>AED</option>
                </select>
              </div>
              <div><label className="gn-overline mb-2 block">Movement</label><input className="gn-input" value={form.movement} onChange={(e) => setForm({...form, movement: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Case material</label><input className="gn-input" value={form.case_material} onChange={(e) => setForm({...form, case_material: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Case size</label><input className="gn-input" value={form.case_size} onChange={(e) => setForm({...form, case_size: e.target.value})} /></div>
              <div><label className="gn-overline mb-2 block">Dial color</label><input className="gn-input" value={form.dial_color} onChange={(e) => setForm({...form, dial_color: e.target.value})} /></div>
              <div className="flex items-end gap-6">
                <label className="flex items-center gap-2 text-sm text-[#f5f5f0]"><input type="checkbox" checked={form.box} onChange={(e) => setForm({...form, box: e.target.checked})} className="accent-[#c9a961]" /> Box</label>
                <label className="flex items-center gap-2 text-sm text-[#f5f5f0]"><input type="checkbox" checked={form.papers} onChange={(e) => setForm({...form, papers: e.target.checked})} className="accent-[#c9a961]" /> Papers</label>
                <label className="flex items-center gap-2 text-sm text-[#f5f5f0]"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} className="accent-[#c9a961]" /> {t('admin.featured')}</label>
              </div>
              <div className="col-span-2">
                <label className="gn-overline mb-2 block">Foto orologio</label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed cursor-pointer transition-colors p-6 text-center ${dragOver ? 'border-[#c9a961] bg-[#0d2a1f]/40' : 'border-[#c9a961]/30 hover:border-[#c9a961]/60'}`}
                  data-testid="admin-watch-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_IMG.join(',')}
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                    data-testid="admin-watch-file-input"
                  />
                  <Upload size={24} className="mx-auto text-[#c9a961] mb-2" strokeWidth={1.2} />
                  <p className="text-sm text-[#f5f5f0]">Trascina qui le foto o clicca per selezionare</p>
                  <p className="text-xs text-[#a3b5ad]/70 mt-1">JPG · PNG · WEBP · max 8 MB · {form.images_list.length} / {MAX_FILES}</p>
                </div>
                {form.images_list.length > 0 && (
                  <div className="mt-4 grid grid-cols-6 gap-2" data-testid="admin-watch-image-grid">
                    {form.images_list.map((src, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden border border-[#c9a961]/20 group">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-[#05150f]/90 border border-[#c9a961]/40 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove"
                          data-testid={`admin-watch-remove-img-${i}`}
                        >
                          <X size={10} className="text-[#c9a961]" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2"><label className="gn-overline mb-2 block">Description (IT)</label><textarea className="gn-textarea" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            </div>
            <div className="flex gap-4 mt-8">
              <button type="button" onClick={close} className="gn-btn-ghost">{t('admin.cancel')}</button>
              <button type="submit" disabled={saving} className="gn-btn-primary" data-testid="admin-watch-save">
                {saving && <Loader2 size={14} className="animate-spin" />}
                {t('admin.save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
